import React, { useState } from 'react';

interface McpTool {
  name: string;
  description?: string;
  inputSchema: {
    type: string;
    properties?: Record<string, any>;
    required?: string[];
  };
}

interface McpSandboxProps {
  endpointUrl: string;
  endpointName: string;
  endpointDescription?: string;
}

const McpSandbox: React.FC<McpSandboxProps> = ({ endpointUrl, endpointName, endpointDescription }) => {
  const [availableTools, setAvailableTools] = useState<McpTool[]>([]);
  const [selectedTool, setSelectedTool] = useState<McpTool | null>(null);
  const [toolParams, setToolParams] = useState<Record<string, any>>({});
  const [isConnecting, setIsConnecting] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'disconnected' | 'connected' | 'error'>('disconnected');
  const [toolResponse, setToolResponse] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const connectToEndpoint = async () => {
    setIsConnecting(true);
    setError(null);
    setToolResponse(null);
    
    try {
      const response = await fetch(endpointUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          jsonrpc: '2.0',
          id: 1,
          method: 'tools/list',
          params: {}
        }),
      });

      const responseText = await response.text();
      
      if (!response.ok) {
        throw new Error(`Failed to connect: ${response.statusText}${responseText ? ` - ${responseText.substring(0, 200)}` : ''}`);
      }

      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        throw new Error(`Invalid JSON response: ${responseText.substring(0, 200)}`);
      }
      
      if (data.result && data.result.tools && Array.isArray(data.result.tools)) {
        setAvailableTools(data.result.tools);
        setConnectionStatus('connected');
      } else if (data.error) {
        throw new Error(`MCP Error: ${data.error.message || JSON.stringify(data.error)}`);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to connect to MCP endpoint');
      setConnectionStatus('error');
      setAvailableTools([]);
    } finally {
      setIsConnecting(false);
    }
  };

  const disconnect = () => {
    setAvailableTools([]);
    setSelectedTool(null);
    setToolParams({});
    setConnectionStatus('disconnected');
    setToolResponse(null);
    setError(null);
  };

  const selectTool = (tool: McpTool) => {
    setSelectedTool(tool);
    setToolResponse(null);
    setError(null);
    
    const initialParams: Record<string, any> = {};
    if (tool.inputSchema.properties) {
      Object.keys(tool.inputSchema.properties).forEach(key => {
        const prop = tool.inputSchema.properties![key];
        if (prop.type === 'boolean') {
          initialParams[key] = false;
        } else if (prop.type === 'number' || prop.type === 'integer') {
          initialParams[key] = 0;
        } else if (prop.type === 'array') {
          initialParams[key] = [];
        } else if (prop.type === 'object') {
          initialParams[key] = {};
        } else {
          initialParams[key] = prop.default || '';
        }
      });
    }
    setToolParams(initialParams);
  };

  const handleToolChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const toolName = e.target.value;
    if (toolName) {
      const tool = availableTools.find(t => t.name === toolName);
      if (tool) {
        selectTool(tool);
      }
    } else {
      setSelectedTool(null);
      setToolParams({});
      setToolResponse(null);
      setError(null);
    }
  };

  const executeTool = async () => {
    if (!selectedTool) return;

    setIsExecuting(true);
    setError(null);
    setToolResponse(null);

    try {
      const response = await fetch(endpointUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          jsonrpc: '2.0',
          id: Date.now(),
          method: 'tools/call',
          params: {
            name: selectedTool.name,
            arguments: toolParams,
          },
        }),
      });

      // Read response as text first, then try to parse as JSON
      const responseText = await response.text();
      
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        // If JSON parsing fails, show the text content
        if (!response.ok) {
          throw new Error(`Tool execution failed: ${response.statusText}${responseText ? ` - ${responseText.substring(0, 200)}` : ''}`);
        }
        throw new Error(`Invalid JSON response: ${responseText.substring(0, 200)}`);
      }

      if (!response.ok) {
        // Extract error message from response
        const errorMsg = data?.error?.message || data?.message || response.statusText || 'Tool execution failed';
        const errorDetails = data?.error?.data ? ` - ${JSON.stringify(data.error.data)}` : '';
        throw new Error(`Tool execution failed: ${errorMsg}${errorDetails}`);
      }
      
      if (data.error) {
        // Handle MCP protocol errors
        const errorMsg = data.error.message || JSON.stringify(data.error);
        const errorDetails = data.error.data ? ` - Details: ${JSON.stringify(data.error.data)}` : '';
        throw new Error(`MCP Error: ${errorMsg}${errorDetails}`);
      }
      
      setToolResponse(data);
    } catch (err: any) {
      setError(err.message || 'Failed to execute tool');
    } finally {
      setIsExecuting(false);
    }
  };

  const handleParamChange = (paramName: string, value: any) => {
    setToolParams(prev => ({
      ...prev,
      [paramName]: value,
    }));
  };

  const parseResultContent = (response: any) => {
    try {
      if (response?.result?.content && Array.isArray(response.result.content)) {
        return response.result.content.map((item: any, index: number) => {
          if (item.type === 'text' && item.text) {
            try {
              const parsed = JSON.parse(item.text);
              return { ...item, parsedJson: parsed };
            } catch {
              return item;
            }
          }
          return item;
        });
      }
      return null;
    } catch {
      return null;
    }
  };

  const renderParamInput = (paramName: string, paramSchema: any) => {
    const value = toolParams[paramName];
    const isRequired = selectedTool?.inputSchema.required?.includes(paramName);

    if (paramSchema.type === 'boolean') {
      return (
        <div className="form-group">
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id={paramName}
              checked={value || false}
              onChange={(e) => handleParamChange(paramName, e.target.checked)}
            />
            <label className="form-check-label" htmlFor={paramName}>
              {paramName} {isRequired && <span className="text-danger">*</span>}
            </label>
          </div>
          {paramSchema.description && (
            <small className="form-text text-muted">{paramSchema.description}</small>
          )}
        </div>
      );
    }

    return (
      <div className="form-group">
        <label htmlFor={paramName}>
          {paramName} {isRequired && <span className="text-danger">*</span>}
        </label>
        <input
          type={paramSchema.type === 'number' || paramSchema.type === 'integer' ? 'number' : 'text'}
          className="form-control"
          id={paramName}
          value={value || ''}
          onChange={(e) => handleParamChange(paramName, 
            paramSchema.type === 'number' || paramSchema.type === 'integer' 
              ? parseFloat(e.target.value) 
              : e.target.value
          )}
          placeholder={paramSchema.description}
        />
        {paramSchema.description && (
          <small className="form-text text-muted">{paramSchema.description}</small>
        )}
      </div>
    );
  };

  return (
    <div className="mcp-sandbox">
      {/* Header with endpoint info */}

      {/* Connection Panel */}
      <div className="card shadow-sm mb-4">
        <div className="card-header py-3 d-flex justify-content-between align-items-center">
          <h6 className="m-0 font-weight-bold text-primary">
            <i className="fas fa-plug mr-2"></i>
            Connection
          </h6>
          {connectionStatus === 'connected' && (
            <span className="badge badge-success">
              <i className="fas fa-check-circle mr-1"></i>
              Connected ({availableTools.length} tools)
            </span>
          )}
        </div>
        <div className="card-body">
          {connectionStatus === 'disconnected' && (
            <button
              className="btn btn-primary btn-block"
              onClick={connectToEndpoint}
              disabled={isConnecting}
            >
              {isConnecting ? (
                <>
                  <span className="spinner-border spinner-border-sm mr-2" role="status"></span>
                  Connecting...
                </>
              ) : (
                <>
                  <i className="fas fa-plug mr-2"></i>
                  Connect & List Tools
                </>
              )}
            </button>
          )}
          {connectionStatus === 'connected' && (
            <button
              className="btn btn-secondary btn-block"
              onClick={disconnect}
            >
              <i className="fas fa-times mr-2"></i>
              Disconnect
            </button>
          )}
          {connectionStatus === 'error' && error && (
            <>
              <div className="alert alert-danger mb-3">
                <i className="fas fa-exclamation-triangle mr-2"></i>
                {error}
              </div>
              <button
                className="btn btn-primary btn-block"
                onClick={connectToEndpoint}
                disabled={isConnecting}
              >
                <i className="fas fa-redo mr-2"></i>
                Retry Connection
              </button>
            </>
          )}
        </div>
      </div>

      {/* Tool Selection Dropdown - Horizontal Layout */}
      {connectionStatus === 'connected' && availableTools.length > 0 && (
        <div className="card shadow-sm mb-4">
          <div className="card-body">
            <div className="form-group mb-0">
              <label htmlFor="tool-select" className="font-weight-bold">
                <i className="fas fa-wrench mr-2"></i>
                Select Tool
              </label>
              <select
                id="tool-select"
                className="form-control dropdown-styling"
                value={selectedTool?.name || ''}
                onChange={handleToolChange}
              >
                <option value="">-- Choose a tool to test --</option>
                {availableTools.map((tool) => (
                  <option key={tool.name} value={tool.name}>
                    {tool.name} {tool.description ? `- ${tool.description}` : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Tool Execution Panel */}
      {selectedTool && (
        <div className="card shadow-sm mb-4">
          <div className="card-header py-3">
            <h6 className="m-0 font-weight-bold text-primary">
              <i className="fas fa-wrench mr-2"></i>
              {selectedTool.name}
            </h6>
          </div>
          <div className="card-body">
            {selectedTool.description && (
              <p className="text-muted mb-3">{selectedTool.description}</p>
            )}

            <h6 className="font-weight-bold mb-3">Parameters</h6>
            {selectedTool.inputSchema.properties && Object.keys(selectedTool.inputSchema.properties).length > 0 ? (
              <div>
                {Object.entries(selectedTool.inputSchema.properties).map(([name, schema]) => (
                  <div key={name} className="mb-3">
                    {renderParamInput(name, schema)}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted">No parameters required</p>
            )}

            <button
              className="btn btn-primary btn-lg btn-block mt-3"
              onClick={executeTool}
              disabled={isExecuting}
            >
              {isExecuting ? (
                <>
                  <span className="spinner-border spinner-border-sm mr-2" role="status"></span>
                  Running...
                </>
              ) : (
                <>
                  <i className="fas fa-play mr-2"></i>
                  Run Tool
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Response Panel */}
      {(toolResponse || error) && selectedTool && (
        <div className="card shadow-sm">
          <div className="card-header py-3">
            <h6 className="m-0 font-weight-bold text-primary">
              <i className="fas fa-terminal mr-2"></i>
              Response
            </h6>
          </div>
          <div className="card-body">
            {error && (
              <div className="alert alert-danger">
                <i className="fas fa-exclamation-triangle mr-2"></i>
                {error}
              </div>
            )}
            {toolResponse && (
              <div>
                {(() => {
                  const parsedContent = parseResultContent(toolResponse);
                  
                  if (parsedContent && parsedContent.length > 0) {
                    return parsedContent.map((item: any, index: number) => (
                      <div key={index} className="mb-3">
                        {item.type === 'text' && (
                          <div>
                            <small className="text-muted d-block mb-2">
                              <i className="fas fa-file-alt mr-1"></i>
                              Text Content {parsedContent.length > 1 ? `(${index + 1}/${parsedContent.length})` : ''}
                            </small>
                            {item.parsedJson ? (
                              <div>
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                  <small className="text-success">
                                    <i className="fas fa-check-circle mr-1"></i>
                                    Valid JSON - Structured View
                                  </small>
                                </div>
                                <pre className="bg-light p-3 rounded" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                  <code>{JSON.stringify(item.parsedJson, null, 2)}</code>
                                </pre>
                              </div>
                            ) : (
                              <pre className="bg-light p-3 rounded" style={{ maxHeight: '400px', overflowY: 'auto', whiteSpace: 'pre-wrap' }}>
                                {item.text}
                              </pre>
                            )}
                          </div>
                        )}
                        {item.type === 'image' && (
                          <div>
                            <small className="text-muted d-block mb-2">
                              <i className="fas fa-image mr-1"></i>
                              Image Content
                            </small>
                            <img src={item.data} alt="Response" className="img-fluid rounded" />
                          </div>
                        )}
                        {item.type !== 'text' && item.type !== 'image' && (
                          <div>
                            <small className="text-muted d-block mb-2">
                              <i className="fas fa-code mr-1"></i>
                              {item.type} Content
                            </small>
                            <pre className="bg-light p-3 rounded" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                              <code>{JSON.stringify(item, null, 2)}</code>
                            </pre>
                          </div>
                        )}
                      </div>
                    ));
                  }
                  
                  return (
                    <div>
                      <small className="text-muted d-block mb-2">
                        <i className="fas fa-code mr-1"></i>
                        Raw Response
                      </small>
                      <pre className="bg-light p-3 rounded" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        <code>{JSON.stringify(toolResponse, null, 2)}</code>
                      </pre>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Empty state when no tool selected */}
      {connectionStatus === 'connected' && !selectedTool && availableTools.length > 0 && (
        <div className="card shadow-sm">
          <div className="card-body text-center py-5">
            <i className="fas fa-arrow-up fa-3x text-muted mb-3"></i>
            <h5 className="text-muted">Select a tool from the dropdown above</h5>
            <p className="text-muted">
              Choose a tool to configure its parameters and execute it.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default McpSandbox;
